import React, { createContext, Suspense, useContext } from 'react';
import { Link, Routes, useLocation } from 'react-router-dom';
import cn from '../utils/cn';
import KoobLoader from './KoobLoader';
import KoobModal from './KoobModal';
import { useShownWhileOpen } from './overlay';
import KoobNavbarAvatar from './KoobNavbarAvatar';

export type KoobSettingsLink = {
  to: string;
  label: string;
  icon?: string;
  /** Paths on which the link is active (the area's pages). */
  activeMatch: string[];
};

export type KoobSettingsGroup = {
  label: string;
  links: KoobSettingsLink[];
};

const InSettingsModal = createContext(false);

/** True inside the settings modal: a page drops its app chrome (TO's
 * KoobLayout renders its header and content only). */
export const useInSettingsModal = () => useContext(InSettingsModal);

/**
 * A settings page's content, under its KoobPageHeader: every page in the
 * modal sits in one, so all share the header's gutter and the same spacing.
 */
export function KoobSettingsBody({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      {children}
    </div>
  );
}

const isActive = (link: KoobSettingsLink, pathname: string) =>
  link.activeMatch.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

const itemClasses = (active: boolean) =>
  cn(
    'flex items-center gap-2.5 px-3 py-2 text-sm transition',
    active
      ? 'bg-gray-200/70 font-semibold text-k-h'
      : 'text-k-p hover:bg-gray-200/40 hover:text-k-h',
  );

/**
 * Settings, the user's and the organization's, in one modal (as in netweak):
 * sections on the left, the section's page on the right. Each app routes its
 * settings pages as children (`<Route>`s); the links are URLs, so a page's own
 * tabs and links keep working inside. Stays mounted: `open` follows the URL,
 * and while it fades out the routes keep the last settings page on screen.
 */
export default function KoobSettingsModal({
  open,
  groups,
  user,
  onClose,
  children,
}: {
  open: boolean;
  groups: KoobSettingsGroup[];
  user: { name?: string; email?: string };
  onClose: () => void;
  children: React.ReactNode;
}) {
  const location = useShownWhileOpen(open, useLocation());
  const { pathname } = location;

  return (
    <KoobModal
      open={open}
      setOpen={onClose}
      showCloseIcon
      widthClass="w-full sm:max-w-7xl"
      paddingClass="p-0"
    >
      <div className="flex h-[85vh] max-h-[860px]">
        <aside className="hidden md:flex w-60 shrink-0 flex-col overflow-y-auto border-r border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <KoobNavbarAvatar name={user.name} size="sm" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-k-h">
                {user.name}
              </div>
              <div className="truncate text-xs text-k-p">{user.email}</div>
            </div>
          </div>

          {groups.map((group, index) => (
            <div key={group.label} className={index === 0 ? 'mt-3' : 'mt-5'}>
              <div className="mb-1 px-3 text-xs font-medium uppercase tracking-wider text-gray-500">
                {group.label}
              </div>
              <nav className="flex flex-col gap-0.5">
                {group.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={cn(
                      itemClasses(isActive(link, pathname)),
                      'rounded-lg',
                    )}
                  >
                    {link.icon && <i className={cn(link.icon, 'fa-fw')} />}
                    <span className="truncate">{link.label}</span>
                  </Link>
                ))}
              </nav>
            </div>
          ))}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Phones: the sections as a row of pills, clear of the close icon. */}
          <nav className="flex md:hidden gap-1 overflow-x-auto border-b border-gray-200 py-2 pl-3 pr-14">
            {groups
              .flatMap((group) => group.links)
              .map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={cn(
                    itemClasses(isActive(link, pathname)),
                    'shrink-0 rounded-full whitespace-nowrap',
                  )}
                >
                  {link.label}
                </Link>
              ))}
          </nav>

          {/* The page's header has its actions top right: pr-16 keeps them
              clear of the close icon. */}
          <div className="flex-1 overflow-y-auto bg-gray-50 md:[&>header]:pr-16">
            <InSettingsModal.Provider value>
              <Suspense fallback={<KoobLoader centered />}>
                <Routes location={location}>{children}</Routes>
              </Suspense>
            </InSettingsModal.Provider>
          </div>
        </div>
      </div>
    </KoobModal>
  );
}
